'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import type { BlogPost } from '@medprev-web-app/api-graphql-client';

import { PostCard } from './post-card.component';

import { Button } from '@/components/ui/button/button.component';
import { useLoading } from '@/hooks/use-loading.hook';
import { Card } from '@/components/ui/card.component';
import { Skeleton } from '@/components/ui/skeleton.component';
import { getBlogPostsPageData } from '@/app/(main)/(pages)/(institucional)/blog/_services/blog-posts-page.service';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';

interface LoadMorePostsProps {
  lastPage: number;
  theme?: string;
}

function LoadMorePostsLoader() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="w-full">
          <Card className="mt-6 flex flex-col px-0 py-0 h-full">
            <Skeleton className="object-cover h-48 w-full rounded-lg" />
            <Skeleton className="mx-6 mt-4 mb-2 h-7 " />
            <Skeleton className="mx-6 h-7 w-1/2 mb-4" />
            <Skeleton className="mx-6 mb-2 h-6" />
            <Skeleton className="mx-6 mb-2 h-6" />
            <Skeleton className="mx-6 mb-2 h-6" />
            <Skeleton className="mx-6 mb-6 h-6" />
            <Skeleton className="mx-6 h-3 w-28" />
          </Card>
        </div>
      ))}
    </>
  );
}

export function LoadMorePosts({ lastPage, theme }: LoadMorePostsProps) {
  const [allPosts, setAllPosts] = useState<BlogPost[]>([]);
  const [page, setPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(lastPage > 1);
  const { isLoading, startLoading, stopLoading } = useLoading();
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    requestIdRef.current += 1;
    setAllPosts([]);
    setPage(1);
    setHasMorePosts(lastPage > 1);
  }, [theme, lastPage]);

  const loadMorePosts = useCallback(async (): Promise<void> => {
    if (!hasMorePosts || isLoading) {
      return;
    }

    startLoading();
    const nextPage = page + 1;
    const currentRequestId = ++requestIdRef.current;

    try {
      const result = await getBlogPostsPageData({
        page: nextPage,
        pageSize: 6,
        theme,
      });

      if (requestIdRef.current !== currentRequestId || !isMountedRef.current) {
        return;
      }

      const newPosts = result?.data || [];
      const validPosts = newPosts.filter((post): post is BlogPost => post !== null);

      if (validPosts.length > 0) {
        setAllPosts((prev) => [...prev, ...validPosts]);
        setPage(nextPage);

        if (nextPage >= lastPage) {
          setHasMorePosts(false);
        }
      } else {
        setHasMorePosts(false);
      }
    } catch (error) {
      if (requestIdRef.current !== currentRequestId || !isMountedRef.current) {
        return;
      }

      const logger = getLogger('LoadMorePostsComponent');
      logger.warn('Error when fetching load more posts', { page: nextPage, theme, error });
      setHasMorePosts(false);
    } finally {
      if (isMountedRef.current) {
        stopLoading();
      }
    }
  }, [hasMorePosts, isLoading, lastPage, page, startLoading, stopLoading, theme]);

  const isLastPage = page >= lastPage || !hasMorePosts;
  const shouldHideComponent = !hasMorePosts && allPosts.length === 0 && !isLoading;

  if (shouldHideComponent) {
    return null;
  }

  return (
    <div className="mt-6">
      {allPosts.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
          {allPosts.map((post) => (
            <PostCard key={post.id} post={post} variant="default" />
          ))}
        </div>
      )}
      {isLoading && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
          <LoadMorePostsLoader />
        </div>
      )}
      {!isLastPage && (
        <div className="w-full flex justify-center pt-6">
          <Button
            className="w-full sm:max-w-75"
            variant="secondary"
            onClick={loadMorePosts}
            disabled={isLoading}
            isLoading={isLoading}
            data-analytics-event={AnalyticsEnum.CLICK_LOAD_MORE}
          >
            Carregar mais
          </Button>
        </div>
      )}
    </div>
  );
}
